import React, { useMemo } from 'react';
import { AlertCircle, Calendar, Check, ReceiptText, X } from 'lucide-react';

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function AccountReconciliationModal({
  open,
  title,
  subtitle,
  documents = [],
  totalAmount = 0,
  onClose,
  onConfirm,
  onAllocationChange,
  confirmLabel = 'Confirmar',
  loading = false,
  emptyMessage = 'No hay comprobantes pendientes para conciliar.',
  helperText = '',
  requiredAllocation = 0,
  onSkip,
  skipLabel = 'No aplicar a ninguna factura',
}) {
  const totals = useMemo(() => {
    const allocated = (Array.isArray(documents) ? documents : []).reduce(
      (acc, document) => acc + Math.max(0, Number(document?.allocation) || 0),
      0,
    );
    const required = Math.max(0, Number(requiredAllocation) || 0);
    const remaining = Math.max(0, Number(totalAmount || 0) - allocated);
    const missingRequired = Math.max(0, required - allocated);
    return { allocated, remaining, missingRequired };
  }, [documents, requiredAllocation, totalAmount]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-slate-950/50 backdrop-blur-[1px] flex items-center justify-center p-4">
      <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col">
        <div className="shrink-0 bg-[#0B2A36] px-5 py-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-white/60">
              Conciliacion
            </p>
            <h2 className="text-lg font-black text-white tracking-tight">
              {title}
            </h2>
            {subtitle ? (
              <p className="text-[13px] text-white/70">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center justify-center shrink-0"
            title="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="shrink-0 px-4 py-3 border-b border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50/60">
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Importe
            </p>
            <p className="text-lg font-black text-slate-800">
              $ {formatCurrency(totalAmount)}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Asignado
            </p>
            <p className="text-lg font-black text-emerald-600">
              $ {formatCurrency(totals.allocated)}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Restante
            </p>
            <p className="text-lg font-black text-amber-600">
              $ {formatCurrency(totals.remaining)}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Requerido
            </p>
            <p className="text-lg font-black text-slate-800">
              $ {formatCurrency(requiredAllocation)}
            </p>
          </div>
        </div>

        {(helperText || totals.missingRequired > 0.000001) ? (
          <div className="shrink-0 px-4 pt-3">
            {helperText ? (
              <div className="mb-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-[11px] font-bold text-blue-700">
                {helperText}
              </div>
            ) : null}

            {totals.missingRequired > 0.000001 ? (
              <div className="mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2">
                <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[11px] font-bold text-amber-800">
                  Falta asignar $ {formatCurrency(totals.missingRequired)} para cubrir el importe aplicable.
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex-1 min-h-0 px-4 pb-4 pt-3 overflow-auto">
          {documents.length === 0 ? (
            <div className="py-16 text-center text-slate-400 flex flex-col items-center">
              <ReceiptText size={32} className="mb-2 opacity-50" />
              <p>{emptyMessage}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((document) => {
                const isSelected = Math.max(0, Number(document.allocation) || 0) > 0.000001;
                return (
                <div
                  key={document.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onAllocationChange?.(document.id, document.open_amount)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onAllocationChange?.(document.id, document.open_amount);
                    }
                  }}
                  title={isSelected ? 'Aplicando esta factura' : 'Clic para aplicar el total pendiente'}
                  className={`rounded-xl border p-3 shadow-sm cursor-pointer transition-all ${
                    isSelected
                      ? 'border-emerald-300 bg-emerald-50/60 ring-1 ring-emerald-200'
                      : 'border-slate-200 bg-white hover:border-[#51A7FD]/50 hover:bg-slate-50'
                  }`}
                >
                  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_180px] gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {isSelected ? (
                          <Check size={14} className="text-emerald-600 shrink-0" />
                        ) : null}
                        <span className="px-2.5 py-1 rounded-lg bg-slate-100 border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-600">
                          {document.kindLabel || 'Comprobante'}
                        </span>
                        {document.numberLabel ? (
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            {document.numberLabel}
                          </span>
                        ) : null}
                      </div>

                      <p className="mt-1.5 text-[13px] font-black text-slate-800 uppercase tracking-tight">
                        {document.title}
                      </p>

                      <div className="mt-1.5 flex flex-wrap gap-3 text-[10px] font-bold text-slate-500 uppercase">
                        {document.dateLabel ? (
                          <span className="flex items-center gap-1">
                            <Calendar size={12} className="text-slate-300" />
                            {document.dateLabel}
                          </span>
                        ) : null}
                        {document.paymentConditionLabel ? (
                          <span>{document.paymentConditionLabel}</span>
                        ) : null}
                        {document.dueLabel ? (
                          <span>Vence {document.dueLabel}</span>
                        ) : null}
                      </div>

                      {document.description ? (
                        <p className="mt-1.5 text-[11px] font-bold text-slate-500">
                          {document.description}
                        </p>
                      ) : null}
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                            Pendiente
                          </p>
                          <p className="text-[15px] font-black text-slate-800">
                            $ {formatCurrency(document.open_amount)}
                          </p>
                        </div>
                        {isSelected ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              onAllocationChange?.(document.id, 0);
                            }}
                            title="Quitar el monto de esta factura"
                            className="h-7 w-7 shrink-0 rounded-lg bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 transition-colors flex items-center justify-center"
                          >
                            <X size={14} />
                          </button>
                        ) : null}
                      </div>

                      <label className="mt-2 block text-[8px] font-black uppercase tracking-widest text-slate-400">
                        Aplicar a este comprobante
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={document.allocation ?? 0}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) =>
                          onAllocationChange?.(document.id, event.target.value)
                        }
                        className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[12px] font-black text-slate-800 outline-none focus:border-[#51A7FD]"
                      />
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="shrink-0 px-4 py-3 border-t border-slate-100 bg-white flex flex-col sm:flex-row gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-3 rounded-2xl border border-slate-200 text-slate-600 font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          {onSkip ? (
            <button
              type="button"
              onClick={onSkip}
              disabled={loading}
              className="px-5 py-3 rounded-2xl border border-[#0B2A36]/20 text-[#0B2A36] font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed sm:mr-auto"
            >
              {skipLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || totals.missingRequired > 0.000001}
            className="px-5 py-3 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] font-black text-[10px] uppercase tracking-widest hover:brightness-95 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="h-4 w-4 rounded-full border-2 border-[#0B2A36]/20 border-t-[#0B2A36] animate-spin"></div>
                Procesando...
              </>
            ) : (
              <>
                <Check size={14} />
                {confirmLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
